import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Typography, message, Space, Row, Col, Statistic, Flex, Input } from 'antd';
import { EyeOutlined, ExportOutlined, FilterOutlined } from '@ant-design/icons';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import dayjs from 'dayjs';
import SupplierInvoiceDetails from '../components/SupplierInvoiceDetails';
import EmptyState from '../components/EmptyState';
import { FileTextOutlined } from '@ant-design/icons';

const { Title } = Typography;

const SupplierInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pageSize, setPageSize] = useState(10);

    // Detail Modal
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/purchase/invoices', {
                headers: { Authorization: `Bearer ${token}` },
                params: { limit: 100 }
            });
            setInvoices(res.data);
        } catch (error) {
            console.error(error);
            message.error('Failed to load supplier invoices');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, []);

    const handleView = (id) => {
        setSelectedInvoiceId(id);
        setDetailsVisible(true);
    };

    const filteredInvoices = invoices.filter(inv =>
        (inv.ref || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (inv.socname || '').toLowerCase().includes(searchText.toLowerCase())
    );

    const totals = filteredInvoices.reduce((acc, inv) => {
        acc.total_ttc += parseFloat(inv.total_ttc || 0);
        return acc;
    }, { total_ttc: 0 });

    const handleExportCSV = () => {
        const headers = ["Ref", "Date", "Supplier", "Status", "Total TTC"];
        const rows = filteredInvoices.map(inv => [
            inv.ref,
            new Date(inv.date * 1000).toLocaleDateString(),
            inv.socname,
            inv.status === '0' ? 'Draft' : (inv.status === '1' ? 'Validated' : 'Paid'),
            inv.total_ttc
        ]);

        const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `supplier_invoices_${dayjs().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        {
            title: 'Ref',
            dataIndex: 'ref',
            key: 'ref',
            render: t => <b style={{ color: '#1890ff' }}>{t}</b>,
            width: 140
        },
        {
            title: 'Date', dataIndex: 'date', key: 'date',
            render: d => <span style={{ color: '#555' }}>{new Date(d * 1000).toLocaleDateString()}</span>,
            width: 120
        },
        {
            title: 'Supplier', dataIndex: 'socname', key: 'socname',
        },
        {
            title: 'Status', dataIndex: 'status', key: 'status',
            render: (s) => {
                if (s === '0') return <Tag color="default">Draft</Tag>;
                if (s === '1') return <Tag color="blue">Validated</Tag>;
                if (s === '2') return <Tag color="success">Paid</Tag>;
                return <Tag>Unknown</Tag>;
            },
            width: 100
        },
        { title: 'Total (TTC)', dataIndex: 'total_ttc', key: 'total_ttc', align: 'right', render: formatCurrency },
        {
            title: 'Actions', key: 'actions',
            align: 'center',
            width: 100,
            render: (_, rec) => (
                <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleView(rec.id)}>View</Button>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Flex justify="space-between" align="center" style={{ marginBottom: 32 }}>
                <div>
                    <div className="section-title">Purchasing</div>
                    <Title level={2} style={{ margin: 0, fontWeight: 700 }}>Supplier Invoices</Title>
                    <Typography.Text type="secondary">View and track stock purchase documents</Typography.Text>
                </div>
                <Space size="middle">
                    <Button icon={<ExportOutlined />} onClick={handleExportCSV}>Export CSV</Button>
                    <Button type="primary" onClick={fetchInvoices} style={{ height: '40px' }}>Refresh</Button>
                </Space>
            </Flex>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={8}>
                    <Card className="premium-card">
                        <Statistic title="Total Purchase Documents" value={filteredInvoices.length} prefix={<FilterOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card className="premium-card">
                        <Statistic title="Total Investment" value={totals.total_ttc} suffix="JOD" valueStyle={{ color: '#3f8600' }} precision={3} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card className="premium-card">
                        <Input.Search
                            placeholder="Ref or Supplier..."
                            allowClear
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: '100%', marginTop: '20px' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card className="premium-card">
                <Table
                    dataSource={filteredInvoices}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    size="middle"
                    onRow={(record) => ({
                        onClick: () => handleView(record.id),
                    })}
                    pagination={{
                        current: 1,
                        pageSize: pageSize,
                        showSizeChanger: true,
                        onShowSizeChange: (current, size) => setPageSize(size),
                        showTotal: (total) => `Total ${total} items`
                    }}
                    locale={{
                        emptyText: <EmptyState
                            message="No supplier invoices found"
                            description="Try changing your search text."
                            icon={<FileTextOutlined />}
                        />
                    }}
                />
            </Card>

            <SupplierInvoiceDetails
                visible={detailsVisible}
                invoiceId={selectedInvoiceId}
                onCancel={() => setDetailsVisible(false)}
            />
        </div>
    );
};

export default SupplierInvoices;
